// libs
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

//global components
import HeartIcon from '@nykaa/ui-components/Icons/heart-plus';
import HeartFilledIcon from '@nykaa/ui-components/Icons/heart-filled';
import { SpotLoader } from '@nykaa/ui-components/Loader';

// styles
import {
  Container,
  LoaderContainer,
  LoginNudgeContainer,
  WishlistButton,
} from 'shared/components/Wishlist/styles';
import { BREAKPOINTS } from 'shared/styles/base';

//components
import LoginNudge from 'shared/components/OTPVerification/LoginNudge';
import Message from 'shared/components/Wishlist/components/Message';

// storage
import {
  removeStoredProductToBeAddedToWishlist,
  setProductToAddToWishlistAfterLogin,
} from 'shared/components/Wishlist/storage';

// constants
import {
  ADD_PRODUCT_TO_WISHLIST_FAILED,
  ADDED_TO_WISHLIST_MESSAGE,
  REMOVE_PRODUCT_FROM_WISHLIST_FAILED,
  REMOVED_FROM_WISHLIST_MESSAGE,
  ITEM_REMOVED_FROM_WISHLIST,
} from './constants';
import { ALIGNMENT_TYPES, TOASTER_TYPES, useToastContext } from 'shared/components/Toast';
import { LOGIN_LOCATIONS } from 'shared/helpers/analytics/constants/loginEvents';

// analytics
import {
  trackAddToWishlistClicked,
  trackRemoveFromWishlistClicked,
} from 'layouts/Wishlist/analytics';

//context
import { useAuthModalWrapperContext } from 'components/AuthModal/AuthModalWrapperContext';

//helpers
import { storeScrollY } from 'shared/helpers/utils/scroll';
import NFA from 'shared/helpers/analytics/NFAnalytics';

//hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// defs
import { WishlistProps } from './types';

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function Wishlist({
  wishlistEnabled,
  isLoggedInUser,
  redirectTo,
  size,
  className,
  productId,
  isWishListProduct,
  displayWishlistMessage = false,
  showAnimationForProductId,
  updateShowAnimationForProductId,
  canShowNotification,
  onClickInteraction = EMPTY_FUNC,
  CustomWishlist,
  dataAtValue = 'wishlist-icon',
  addToWishlist,
  removeFromWishlist,
  onWishlistClick,
  isUserFetching = false,
  trackingMetaData,
  showWishlistNudge,
  wishlistNudgeCount,
  isNewWishlistNudge = false,
}: WishlistProps) {
  // contexts
  const theme: Theme = useTheme();
  const { showToast } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);

  const { deviceWidth } = useResponsiveContext();
  const isMobile = deviceWidth < BREAKPOINTS.DESKTOP;

  const { passiveShowAuthModal } = useAuthModalWrapperContext();

  const [shouldShowLoginNudge, setShowLoginNudge] = useState(false);
  const { showBottomSheetAuth } = useAuthModalWrapperContext();
  const addOrRemoveMessage = isWishListProduct
    ? ADDED_TO_WISHLIST_MESSAGE
    : REMOVED_FROM_WISHLIST_MESSAGE;

  const [showMessage, setShowMessage] = useState(false);

  const hideLoginNudge = () => {
    setShowLoginNudge(false);
    removeStoredProductToBeAddedToWishlist();
  };

  const addProductToWishlist = useCallback(() => {
    addToWishlist({ itemId: productId })
      .then(() => {
        setShowMessage(true);
        setIsLoading(false);

        // track adding product to wishlist
        trackAddToWishlistClicked({
          ...trackingMetaData,
          productID: productId,
        });

        // show toast
        canShowNotification &&
          showToast({ type: TOASTER_TYPES.INFO, message: ADDED_TO_WISHLIST_MESSAGE });
        isNewWishlistNudge && showWishlistNudge(wishlistNudgeCount + 1);
      })
      .catch((error: Error) => {
        setIsLoading(false);
        setShowMessage(false);

        // toast for failure
        showToast({
          type: TOASTER_TYPES.ERROR,
          message: error.message || ADD_PRODUCT_TO_WISHLIST_FAILED,
          textAlign: ALIGNMENT_TYPES.LEFT,
          statusCode: error?.statusCode,
        });
      });
  }, [
    addToWishlist,
    productId,
    trackingMetaData,
    canShowNotification,
    showToast,
    isNewWishlistNudge,
    showWishlistNudge,
    wishlistNudgeCount,
  ]);

  const removeProductFromWishlist = useCallback(() => {
    removeFromWishlist({ itemId: productId })
      .then(() => {
        setShowMessage(true);
        setIsLoading(false);

        // track removing product from wishlist
        trackRemoveFromWishlistClicked({
          ...trackingMetaData,
          productID: productId,
        });

        // show toast
        canShowNotification &&
          showToast({ type: TOASTER_TYPES.INFO, message: REMOVED_FROM_WISHLIST_MESSAGE });

        // item removed from wishlist
        {
          isNewWishlistNudge &&
            showToast({
              type: TOASTER_TYPES.INFO,
              textAlign: ALIGNMENT_TYPES.CENTER,
              message: ITEM_REMOVED_FROM_WISHLIST,
              hideIcon: true,
            });
          showWishlistNudge(0);
        }
      })
      .catch((error: Error) => {
        setIsLoading(false);

        // toast for failure
        showToast({
          type: TOASTER_TYPES.ERROR,
          textAlign: ALIGNMENT_TYPES.LEFT,
          message: error?.message || REMOVE_PRODUCT_FROM_WISHLIST_FAILED,
          statusCode: error?.statusCode,
        });
      });
  }, [
    removeFromWishlist,
    productId,
    trackingMetaData,
    canShowNotification,
    showToast,
    isNewWishlistNudge,
    showWishlistNudge,
  ]);

  const addOrRemoveProduct = useCallback(() => {
    setIsLoading(true);

    if (isWishListProduct) {
      removeProductFromWishlist();
    } else {
      addProductToWishlist();
    }
  }, [addProductToWishlist, isWishListProduct, removeProductFromWishlist]);

  const guestUserClicked = useCallback(() => {
    if (isMobile) {
      showBottomSheetAuth('', true);
    } else {
      passiveShowAuthModal();
    }

    setProductToAddToWishlistAfterLogin(productId);
  }, [isMobile, passiveShowAuthModal, productId, showBottomSheetAuth]);

  let wishListAction = useRef({ hasInteracted: false });

  const performWishListAction = useCallback(() => {
    isLoggedInUser ? addOrRemoveProduct() : guestUserClicked();
    wishListAction.current.hasInteracted = false;
  }, [addOrRemoveProduct, guestUserClicked, isLoggedInUser]);

  useEffect(() => {
    if (wishListAction.current.hasInteracted) {
      if (!isUserFetching) {
        performWishListAction();
      } else setIsLoading(true);
    }
  }, [addOrRemoveProduct, guestUserClicked, isLoggedInUser, isUserFetching, performWishListAction]);

  const handleOnWishlistClicked = (event: any) => {
    NFA.updateDataLayer({
      loginPage: '',
      loginLocation: LOGIN_LOCATIONS.WISHLIST_ICON_PRODUCT,
    });

    onWishlistClick && onWishlistClick(isWishListProduct ? 'remove' : 'add');

    event.preventDefault();
    event.stopPropagation();
    //setting scrollY position when wishlist icon is clicked
    storeScrollY(window.location.pathname, window.pageYOffset);
    onClickInteraction();
    if (isUserFetching) {
      setIsLoading(true);
      wishListAction.current.hasInteracted = true;
    } else performWishListAction();
  };

  const onMessageAnimationEnd = () => {
    setShowMessage(false);
  };

  useEffect(() => {
    if (showAnimationForProductId != productId) return;

    // show toast
    canShowNotification &&
      showToast({ type: TOASTER_TYPES.INFO, message: ADDED_TO_WISHLIST_MESSAGE });
    isNewWishlistNudge && showWishlistNudge(wishlistNudgeCount + 1);

    setShowMessage(true);
    updateShowAnimationForProductId('');
    trackAddToWishlistClicked({ ...trackingMetaData, productID: productId });
  }, [
    productId,
    showAnimationForProductId,
    updateShowAnimationForProductId,
    canShowNotification,
    showToast,
    trackingMetaData,
    showWishlistNudge,
    wishlistNudgeCount,
    isNewWishlistNudge,
  ]);

  const isEnabled = isMobile ? wishlistEnabled.mobile : wishlistEnabled.desktop;

  if (!isEnabled) return null;

  return (
    <>
      <Container className={className} data-at={dataAtValue}>
        {CustomWishlist ? (
          <CustomWishlist handleClick={handleOnWishlistClicked} isUpdating={isLoading} />
        ) : (
          <WishlistButton onClick={handleOnWishlistClicked} className={'circle'}>
            {isLoading ? (
              <LoaderContainer>
                <SpotLoader size="small" />
              </LoaderContainer>
            ) : isWishListProduct ? (
              <HeartFilledIcon color={theme.colors.secondary} size={`${size}px`} />
            ) : (
              <HeartIcon color={theme.colors.primary} size={`${size}px`} />
            )}
          </WishlistButton>
        )}
      </Container>

      {displayWishlistMessage && showMessage && !isNewWishlistNudge && (
        <Message
          onHide={onMessageAnimationEnd}
          message={addOrRemoveMessage}
          key={addOrRemoveMessage}
        />
      )}

      {shouldShowLoginNudge && (
        <LoginNudgeContainer onClick={(e) => e.stopPropagation()}>
          <LoginNudge onNudgeHide={hideLoginNudge} redirectTo={redirectTo} />
        </LoginNudgeContainer>
      )}
    </>
  );
}

export default memo(Wishlist);
